import { Box, Center, Image, useMediaQuery, VStack } from '@chakra-ui/react';
import { Button, Text } from '@diamond/shared/ui';
import { FC } from 'react';

interface ErrorComponentProps {
  message: string;
  subtitle?: string;
  buttonTitle?: string;
  action?: () => void;
  showIllustration?: boolean;
  variant?: 'page' | 'section' | 'inline';
}

/**
 * Reusable error component for displaying errors in different contexts
 *
 * Variants:
 * - 'page': Full-page error (uses page-level styling)
 * - 'section': Section-level error (moderate padding, can have illustration)
 * - 'inline': Inline error (minimal styling, no illustration)
 */
export const ErrorComponent: FC<ErrorComponentProps> = ({
  message,
  subtitle,
  buttonTitle = 'Coba Lagi',
  action,
  showIllustration = false,
  variant = 'section',
}) => {
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  const getVariantStyles = () => {
    switch (variant) {
      case 'page':
        return {
          padding: { base: 10, md: 20 },
          minHeight: '60vh',
          illustrationHeight: isMobile ? '220px' : '340px',
        };
      case 'section':
        return {
          padding: { base: 6, md: 8 },
          minHeight: 'auto',
          illustrationHeight: isMobile ? '120px' : '180px',
        };
      case 'inline':
        return {
          padding: { base: 4, md: 6 },
          minHeight: 'auto',
          illustrationHeight: isMobile ? '80px' : '120px',
        };
      default:
        return {
          padding: { base: 6, md: 8 },
          minHeight: 'auto',
          illustrationHeight: isMobile ? '120px' : '180px',
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <Box
      py={styles.padding}
      bg={variant === 'inline' ? 'red.50' : 'white'}
      borderRadius="md"
      border={variant === 'inline' ? '1px' : 'none'}
      borderColor={variant === 'inline' ? 'red.200' : 'transparent'}
      minH={styles.minHeight}
    >
      <Center>
        <VStack spacing={4} align="center" maxW="600px">
          {showIllustration && variant !== 'inline' && (
            <Box py={variant === 'page' ? 10 : 4}>
              <Image
                src="/assets/images/error_Illustration.svg"
                alt="Error illustration"
                height={styles.illustrationHeight}
              />
            </Box>
          )}

          <Box textAlign="center">
            <Text
              fontSize={
                variant === 'page'
                  ? { base: '2xl', md: '3xl' }
                  : variant === 'section'
                  ? { base: 'xl', md: '2xl' }
                  : { base: 'lg', md: 'xl' }
              }
              fontWeight="bold"
              color={variant === 'inline' ? 'red.600' : 'red.500'}
            >
              {message}
            </Text>

            {subtitle && (
              <Text
                fontSize={
                  variant === 'page'
                    ? { base: 'md', md: 'xl' }
                    : variant === 'section'
                    ? { base: 'sm', md: 'lg' }
                    : { base: 'xs', md: 'sm' }
                }
                fontWeight="medium"
                color={variant === 'inline' ? 'gray.700' : 'gray.600'}
                mt={2}
              >
                {subtitle}
              </Text>
            )}
          </Box>

          {action && buttonTitle && (
            <Box pt={2}>
              <Button
                bg={variant === 'inline' ? 'red' : 'blue'}
                color="white"
                variant="solid"
                size={variant === 'page' ? 'lg' : 'md'}
                _hover={{ bg: variant === 'inline' ? 'red.600' : 'navy' }}
                type="button"
                px={variant === 'page' ? 10 : 6}
                onClick={action}
              >
                {buttonTitle}
              </Button>
            </Box>
          )}
        </VStack>
      </Center>
    </Box>
  );
};
